import graphql from "babel-plugin-relay/macro";
import { Suspense, useCallback, useEffect } from "react";
import {
  PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";

import { packageIsAvailableForSale } from "@whoosh/features/src/Packages";

import { StorePackageModal } from "./StorePackageModal";
import { StoreLinkedMembershipModalQuery } from "./__generated__/StoreLinkedMembershipModalQuery.graphql";
import { StoreLinkedMembershipModal_query$key } from "./__generated__/StoreLinkedMembershipModal_query.graphql";

type Props = {
  membershipId: string;
  onClose: () => void;
};

export const StoreLinkedMembershipModal = ({
  membershipId,
  onClose,
}: Props) => {
  const [queryRef, loadQuery] = useQueryLoader<StoreLinkedMembershipModalQuery>(
    StoreLinkedMembershipModalQueryString
  );

  const loadMembership = useCallback(() => {
    loadQuery({ membershipId }, { fetchPolicy: "store-and-network" });
  }, [loadQuery, membershipId]);

  useEffect(() => {
    loadMembership();
  }, [loadMembership]);

  return (
    <Suspense fallback={null}>
      {queryRef ? (
        <StoreLinkedMembershipModalInner
          queryRef={queryRef}
          onClose={onClose}
          onAuthComplete={loadMembership}
        />
      ) : null}
    </Suspense>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<StoreLinkedMembershipModalQuery>;
  onClose: () => void;
  onAuthComplete: () => void;
};

const StoreLinkedMembershipModalInner = ({
  queryRef,
  onClose,
  onAuthComplete,
}: InnerProps) => {
  const data = usePreloadedQuery(
    StoreLinkedMembershipModalQueryString,
    queryRef
  );

  return (
    <StoreLinkedMembershipModalContent
      queryRef={data}
      onClose={onClose}
      onAuthComplete={onAuthComplete}
    />
  );
};

type ContentProps = {
  queryRef: StoreLinkedMembershipModal_query$key;
  onClose: () => void;
  onAuthComplete: () => void;
};

export const StoreLinkedMembershipModalContent = ({
  queryRef,
  onClose,
  onAuthComplete,
}: ContentProps) => {
  const data = useFragment(StoreLinkedMembershipModalFragmentString, queryRef);
  const membership = data.membership;
  const isMembership = membership?.__typename === "MembershipPackage";
  const isSaleEnabled =
    isMembership &&
    (membership.storeAvailability === "PUBLIC" ||
      membership.storeAvailability === "PRIVATE");
  const isAvailable =
    isSaleEnabled &&
    membership.assignable &&
    packageIsAvailableForSale(membership, membership.club.timeZone);

  useEffect(() => {
    if (!isAvailable) {
      onClose();
    }
  }, [isAvailable, onClose]);

  return isAvailable ? (
    <StorePackageModal
      packageRef={membership}
      sessionRef={data.session}
      isOpen
      onClose={onClose}
      onAuthComplete={onAuthComplete}
    />
  ) : null;
};

const StoreLinkedMembershipModalQueryString = graphql`
  query StoreLinkedMembershipModalQuery($membershipId: ID!) {
    ...StoreLinkedMembershipModal_query @arguments(membershipId: $membershipId)
  }
`;

const StoreLinkedMembershipModalFragmentString = graphql`
  fragment StoreLinkedMembershipModal_query on RootQueryType
  @argumentDefinitions(membershipId: { type: "ID!" }) {
    session {
      ...StorePackageModal_session
    }
    membership: node(id: $membershipId) {
      __typename
      ... on MembershipPackage {
        assignable
        storeAvailability
        club {
          timeZone
        }
        ...packageIsAvailableForSale_package
        ...StorePackageModal_package
      }
    }
  }
`;
